<template>
  <master-layout :model-value="activeStepForMenu" :navigation="steps" @update:model-value="updateActiveStep">
    <template #header>
      <div class="d-flex justify-space-between align-center pr-1">
        <p class="caption">
          {{
            isEdit
              ? `Редактирование подключения ${integration?.master?.steps.integrationSettings?.connectionName[0].value || ''}`
              : 'Подключение менеджера каналов'
          }}
        </p>
        <confirmation-dialog
          :action="close"
          :auto-confirm="
            (channelManagerCode === ChannelManagerCode.Bnovo || channelManagerCode === ChannelManagerCode.Kontur) &&
            activeStep === 6
          "
          confirm-text="Прервать"
          confirm-btn-variant="primary"
        >
          <template #activator>
            <e-btn append-icon-src="closeSvg" variant="context" size="M">
              <template v-if="$vuetify.display.lgAndUp"> Закрыть</template>
            </e-btn>
          </template>
          <div class="h3 mb-6">Прерывание работы мастера</div>
          <div class="paragraph-l text-secondary">
            {{ closeConfirmationText }}
            <div class="mt-4">Вы уверены, что хотите завершить работу мастера?</div>
          </div>
        </confirmation-dialog>
      </div>
    </template>

    <template v-if="activeStep !== 0" #subheader>
      <div v-if="channelManagerCode === ChannelManagerCode.TravelLine" class="d-flex align-center">
        <v-img width="22" :src="TravelLineImage" />
        <div class="text-secondary button-l ml-2">TravelLine</div>
      </div>
      <div v-else-if="channelManagerCode === ChannelManagerCode.Exely" class="d-flex align-center">
        <v-img width="22" :src="ExelyImage" />
        <div class="text-secondary button-l ml-2">Exely</div>
      </div>
      <div v-else-if="channelManagerCode === ChannelManagerCode.Bnovo" class="d-flex align-center">
        <v-img width="22" :src="BnovoImage" />
        <div class="text-secondary button-l ml-2">Bnovo</div>
      </div>
      <div v-else-if="channelManagerCode === ChannelManagerCode.Kontur" class="d-flex align-center">
        <v-img width="22" :src="KonturImage" />
        <div class="text-secondary button-l ml-2">Контур Отель</div>
      </div>
    </template>

    <template v-if="activeStep === 0">
      <div class="h3 mb-4">Выберите менеджер каналов для подключения</div>
      <div class="paragraph-s_h text-secondary mb-10">
        Загрузите информацию о вашем отеле и тарифные планы с помощью менеджера каналов. Если у вас нет менеджера
        каналов, перейдите в подраздел «Ручное&nbsp;управление»
      </div>
      <select-menu :items="menu" @item-click="onMenuItemClick" />
    </template>

    <template v-else>
      <t-l-integration
        v-if="channelManagerCode === ChannelManagerCode.TravelLine || channelManagerCode === ChannelManagerCode.Exely || channelManagerCode === ChannelManagerCode.RealtyCalendar"
        :is-edit="isEdit"
      />
      <bnovo-integration
        v-else-if="channelManagerCode === ChannelManagerCode.Bnovo || channelManagerCode === ChannelManagerCode.Kontur"
        :is-edit="isEdit"
      />
    </template>

    <t-l-side-error-dialog @close="close" />
  </master-layout>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, toRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import type { SelectMenuItem } from '@/core/types/components/selectMenu';
import BnovoImage from '@/assets/img/pages/tariffs/channel-manager/bnovo.svg';
import Bnovo from '@/assets/img/pages/tariffs/channel-manager/bnovo.svg?component';
import ExelyImage from '@/assets/img/pages/tariffs/channel-manager/exely.svg';
import ExelyIcon from '@/assets/img/pages/tariffs/channel-manager/exely.svg?component';
import KonturImage from '@/assets/img/pages/tariffs/channel-manager/kontur.svg';
import KonturIcon from '@/assets/img/pages/tariffs/channel-manager/kontur.svg?component';
import TravelLineImage from '@/assets/img/pages/tariffs/channel-manager/travel_line.svg';
import TravelLineIcon from '@/assets/img/pages/tariffs/channel-manager/travel_line.svg?component';
import SelectMenu from '@/core/components/common/SelectMenu.vue';
import ConfirmationDialog from '@/core/components/modals/ConfirmationDialog.vue';
import { EBtn } from '@/core/components/ui';
import { useUpdateQuery } from '@/core/composables/router';
import { getConfigValue } from '@/core/consts/config';
import MasterLayout from '@/core/layouts/components/master/MasterLayout.vue';
import { RouteNames } from '@/core/plugins/router/routeNames';
import { ChannelManagerCode } from '@/graphql/generated';
import { useAccommodationObjectsStore } from '@/parts/objects/store/accommodationObjects';
import BnovoIntegration from '@/parts/tariffs/components/CM/Reverse/index.vue';
import TLSideErrorDialog from '@/parts/tariffs/components/CM/TravelLine/TLSideErrorDialog.vue';
import TLIntegration from '@/parts/tariffs/components/CM/TravelLine/index.vue';
import { useReverseIntegrationStore } from '@/parts/tariffs/store/reverse';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';

const $route = useRoute();
const $router = useRouter();

const updateQuery = useUpdateQuery();

const props = defineProps<{
  isEdit?: boolean;
}>();

const {
  clearIntegration: clearTlIntegration,
  integration,
  activeStep: tlActiveStep,
  completedSteps: tlCompletedSteps,
  channelManagerCode: tlChannelManagerCode,
  setChannelManagerCode: setTlChannelManagerCode,
} = useTravelLineStore(props.isEdit);

const {
  activeStep: bnovoActiveStep,
  completedSteps: bnovoCompletedSteps,
  clearIntegration: clearBnovoIntegration,
  channelManagerCode: bnovoChannelManagerCode,
  setChannelManagerCode: setBnovoChannelManagerCode,
} = useReverseIntegrationStore(props.isEdit);

const channelManagerCode = computed({
  get() {
    if (tlChannelManagerCode.value) {
      return tlChannelManagerCode.value;
    }

    return bnovoChannelManagerCode.value;
  },

  set(value: ChannelManagerCode) {
    if (value === ChannelManagerCode.TravelLine || value === ChannelManagerCode.Exely) {
      setBnovoChannelManagerCode(undefined);
      setTlChannelManagerCode(value);
    } else {
      setTlChannelManagerCode(undefined);
      setBnovoChannelManagerCode(value);
    }
  },
});

const activeStep = computed({
  get() {
    if (
      channelManagerCode.value === ChannelManagerCode.TravelLine ||
      channelManagerCode.value === ChannelManagerCode.Exely || 
      channelManagerCode.value === ChannelManagerCode.RealtyCalendar
    ) {
      return tlActiveStep.value;
    }

    return bnovoActiveStep.value;
  },
  set(value: number) {
    if (
      channelManagerCode.value === ChannelManagerCode.TravelLine ||
      channelManagerCode.value === ChannelManagerCode.Exely || 
      channelManagerCode.value === ChannelManagerCode.RealtyCalendar
    ) {
      tlActiveStep.value = value;
    } else {
      bnovoActiveStep.value = value;
    }
  },
});

const completedSteps = computed(() => {
  if (
    channelManagerCode.value === ChannelManagerCode.TravelLine ||
    channelManagerCode.value === ChannelManagerCode.Exely ||
    channelManagerCode.value === ChannelManagerCode.RealtyCalendar
  ) {
    return tlCompletedSteps.value;
  }

  return bnovoCompletedSteps.value;
});

const updateActiveStep = (value: number) => {
  if (props.isEdit) {
    if (
      channelManagerCode.value === ChannelManagerCode.TravelLine ||
      channelManagerCode.value === ChannelManagerCode.Exely ||
      channelManagerCode.value === ChannelManagerCode.RealtyCalendar
    ) {
      activeStep.value = value + 4;
    } else {
      activeStep.value = value + 2;
    }
  } else {
    activeStep.value = value;
  }
};

const { getActiveObject } = useAccommodationObjectsStore();

await getActiveObject();

if ($route.query.manager) {
  channelManagerCode.value = $route.query.manager as ChannelManagerCode;
  activeStep.value = 1;
}

onBeforeUnmount(() => {
  clearTlIntegration();
  clearBnovoIntegration();
});

const closeConfirmationText = toRef(() => {
  if (channelManagerCode.value === ChannelManagerCode.Bnovo && activeStep.value < 6) {
    return 'При возобновлении работы мастера вы будете возвращены на данный шаг.';
  }

  if (props.isEdit) {
    if (activeStep.value < 6) {
      return 'Все внесенные изменения будут сохранены, но не вступят всилу, пока вы не завершите изменение настроек.';
    }

    return 'Все введённые настройки на этом шаге будут потеряны. При возобновлении работы вы будете возвращены на данный шаг.';
  }

  if (activeStep.value < 3) {
    return 'Все введённые настройки не будут сохранены.';
  }

  if (activeStep.value > 3 && activeStep.value < 6) {
    return 'При возобновлении работы мастера вы будете возвращены на данный шаг.';
  }

  return `Все введённые настройки на шаге ${activeStep.value + 1} будут потеряны. При возобновлении работы вы будете возвращены на данный шаг.`;
});

const close = () => {
  if ($route.query.t_id) {
    $router.push({ name: RouteNames.TariffInfo, params: { t_id: $route.query.t_id as string } });
  } else {
    $router.push({ name: RouteNames.CMList });
  }
};

const steps = computed(() => {
  if (
    channelManagerCode.value === ChannelManagerCode.TravelLine ||
    channelManagerCode.value === ChannelManagerCode.Exely || 
    channelManagerCode.value === ChannelManagerCode.RealtyCalendar 
  ) {
    const steps: {
      title: string;
      disabled?: boolean;
    }[] = [
      {
        title: 'Сопоставление категорий номеров',
        disabled: completedSteps.value < 3,
      },
      {
        title: 'Сопоставление групп услуг',
        disabled: completedSteps.value < 4,
      },
      {
        title: 'Сопоставление типов туриста',
        disabled: completedSteps.value < 5,
      },
      {
        title: 'Сопоставление вариантов размещения',
        disabled: completedSteps.value < 6,
      },
    ];

    if (!props.isEdit) {
      steps.unshift(
        ...[
          {
            title: 'Выбор менеджера каналов',
          },
          {
            title: 'Выбор отеля для подключения',
            disabled: !channelManagerCode.value,
          },
          {
            title:
              channelManagerCode.value === ChannelManagerCode.TravelLine
                ? 'Настройки на стороне TL'
                : 'Настройки на стороне Exely',
            disabled: completedSteps.value < 1,
          },
          {
            title: 'Настройка подключения к менеджеру каналов',
            disabled: completedSteps.value < 2,
          },
        ],
      );
    }

    return steps;
  }

  if (channelManagerCode.value === ChannelManagerCode.Bnovo || channelManagerCode.value === ChannelManagerCode.Kontur) {
    const steps = [
      {
        title: 'Категории номеров',
        disabled: completedSteps.value < 1 || activeStep.value === 6,
      },
      {
        title: 'Группы услуг',
        disabled: completedSteps.value < 2 || activeStep.value === 6,
      },
      {
        title: 'Информация о туристах',
        disabled: completedSteps.value < 3 || activeStep.value === 6,
      },
      {
        title: 'Варианты размещения',
        disabled: completedSteps.value < 4 || activeStep.value === 6,
      },
      {
        title: `Настройка в ${channelManagerCode.value === ChannelManagerCode.Bnovo ? 'BNOVO' : 'Контур Отель'}`,
        disabled: completedSteps.value < 5,
      },
    ];

    if (!props.isEdit) {
      steps.unshift(
        {
          title: 'Выбор менеджера каналов',
          disabled: activeStep.value === 6,
        },
        {
          title: 'Настройка подключения к менеджеру каналов',
          disabled: activeStep.value === 6,
        },
      );
    }

    return steps;
  }

  return [
    {
      title: 'Выбор менеджера каналов',
    },
    {
      title: 'Последовательность шагов мастера зависит от выбора менеджера каналов',
      disabled: true,
      noNumber: true,
    },
  ];
});

const onMenuItemClick = async (item: SelectMenuItem<{ id: ChannelManagerCode }>) => {
  await updateQuery({
    manager: undefined,
    interactionId: undefined,
  });

  if (
    channelManagerCode.value === ChannelManagerCode.TravelLine ||
    channelManagerCode.value === ChannelManagerCode.Exely ||
    channelManagerCode.value === ChannelManagerCode.RealtyCalendar
  ) {
    clearTlIntegration();
  } else {
    clearBnovoIntegration();
  }

  channelManagerCode.value = item.id;

  activeStep.value = 1;
};

const activeStepForMenu = computed(() => {
  if (!props.isEdit) {
    return activeStep.value;
  }

  if (
    channelManagerCode.value === ChannelManagerCode.TravelLine ||
    channelManagerCode.value === ChannelManagerCode.Exely ||
    channelManagerCode.value === ChannelManagerCode.RealtyCalendar
  ) {
    return activeStep.value - 4;
  }

  return activeStep.value - 2;
});

const menu = [
  {
    title: 'TravelLine',
    icon: TravelLineIcon,
    id: ChannelManagerCode.TravelLine,
  },
  {
    title: 'Exely',
    icon: ExelyIcon,
    id: ChannelManagerCode.Exely,
  },
  {
    title: 'Bnovo',
    icon: Bnovo,
    id: ChannelManagerCode.Bnovo,
  },
  {
    title: 'Контур Отель',
    icon: KonturIcon,
    id: ChannelManagerCode.Kontur,
  }
];
</script>

<style scoped lang="scss"></style>
